import { useEffect } from 'react';
import type { BlogMeta } from './types.js';

const SITE_URL = 'https://agentbrush.dev';

function buildJsonLd(meta: BlogMeta, url: string, image: string): Record<string, unknown> {
  const base = {
    '@context': 'https://schema.org',
    headline: meta.title,
    description: meta.description,
    image,
    datePublished: meta.date,
    dateModified: meta.updated ?? meta.date,
    author: { '@type': 'Organization', name: meta.author ?? 'AgentBrush' },
    publisher: {
      '@type': 'Organization',
      name: 'AgentBrush',
      logo: { '@type': 'ImageObject', url: `${SITE_URL}/og-image.png` },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
  };
  if (meta.schema === 'HowTo') return { ...base, '@type': 'HowTo', name: meta.title };
  return { ...base, '@type': 'BlogPosting' };
}

/**
 * Per-article document head: title, description, canonical, Open Graph, Twitter,
 * and Article/HowTo JSON-LD. Imperative + restored on unmount, mirroring
 * features/landing/landing-head.tsx (the app is a client SPA with no SSR).
 */
export function BlogPostHead({ meta }: { meta: BlogMeta }): null {
  useEffect(() => {
    const url = `${SITE_URL}/blog/${meta.slug}`;
    // Shared brand OG card for every article (the per-article /og/blog/*.png
    // images are not generated; revisit if real per-article cards are added).
    const image = `${SITE_URL}/og-image.png?v=2`;
    const tags: Record<string, string> = {
      description: meta.description,
      'og:title': meta.title,
      'og:description': meta.description,
      'og:type': 'article',
      'og:url': url,
      'og:image': image,
      'twitter:card': 'summary_large_image',
      'twitter:title': meta.title,
      'twitter:description': meta.description,
      'twitter:image': image,
      'article:published_time': meta.date,
      'article:modified_time': meta.updated ?? meta.date,
    };

    const previousTitle = document.title;
    document.title = `${meta.title} | AgentBrush`;

    const created: HTMLElement[] = [];
    const restored: Array<{ el: HTMLMetaElement; content: string | null }> = [];

    for (const [key, content] of Object.entries(tags)) {
      const attr = key.startsWith('og:') || key.startsWith('article:') ? 'property' : 'name';
      let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
      if (el) {
        restored.push({ el, content: el.getAttribute('content') });
      } else {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute('content', content);
    }

    // Canonical link (create if absent, otherwise patch + restore).
    const canonicalHref = meta.canonical && meta.canonical.length > 0 ? meta.canonical : url;
    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const canonicalCreated = canonical === null;
    const prevCanonicalHref = canonical?.getAttribute('href') ?? null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalHref);

    const ldScript = document.createElement('script');
    ldScript.type = 'application/ld+json';
    ldScript.textContent = JSON.stringify(buildJsonLd(meta, url, image));
    document.head.appendChild(ldScript);
    created.push(ldScript);

    return () => {
      document.title = previousTitle;
      for (const el of created) el.remove();
      for (const { el, content } of restored) {
        if (content !== null) el.setAttribute('content', content);
        else el.removeAttribute('content');
      }
      if (canonical) {
        if (canonicalCreated) canonical.remove();
        else if (prevCanonicalHref !== null) canonical.setAttribute('href', prevCanonicalHref);
      }
    };
  }, [meta]);

  return null;
}
